import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminReferrals() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const isAdmin = user && (user.role === "admin" || user.is_owner);

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const [rowsRes, reportRes] = await Promise.all([
        api.get(`/admin/referrals${params}`),
        api.get("/admin/referrals/report"),
      ]);
      setRows(rowsRes.data?.items || []);
      setReport(reportRes.data || null);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [statusFilter, isAdmin]);

  if (user === null) return <div className="p-10 text-center text-sm text-brand-muted">Loading…</div>;
  if (!isAdmin) {
    return (
      <div data-testid="admin-access-denied" className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif text-3xl mb-3">Admin access required</h2>
        <a href="/projects" className="inline-block text-sm border border-brand-teal px-5 py-2 text-brand-teal hover:bg-brand-teal hover:text-white">Back</a>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-referrals-page">
      <div className="mb-4">
        <Link to="/admin/membership-applications" className="text-xs text-brand-teal hover:underline">← Back to admin</Link>
      </div>
      <header className="mb-4">
        <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">Admin · ColourMatch Pro</div>
        <h1 className="text-2xl font-bold text-brand-navy">Referrals</h1>
      </header>

      {report && (
        <div className="grid sm:grid-cols-4 gap-3 mb-6" data-testid="referrals-report">
          <Stat label="Total invitations" value={report.total_referrals} />
          <Stat label="Activated" value={report.activated_count} />
          <Stat label="Conversion rate" value={`${report.conversion_rate_percent}%`} />
          <Stat label="Discount distributed" value={`A$${report.total_discount_distributed_aud.toFixed(2)}`} />
        </div>
      )}

      <div className="flex items-center gap-3 mb-3">
        <label className="text-xs">
          <span className="font-semibold text-brand-navy mr-2">Filter:</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-brand-border rounded px-2 py-1" data-testid="referrals-status-filter">
            <option value="">All statuses</option>
            {["invited", "opened", "registered", "submitted", "approved", "activated", "declined", "expired"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <span className="text-xs text-gray-600" data-testid="referrals-count">
          {loading ? "Loading…" : `${rows.length} referral${rows.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <div className="border border-brand-border rounded-xl overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-brand-cream text-xs uppercase tracking-widest text-brand-navy">
            <tr>
              <th className="text-left px-3 py-2">Homeowner</th>
              <th className="text-left px-3 py-2 hidden sm:table-cell">Painter</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">Method</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2 hidden lg:table-cell">Discount</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">Sent</th>
            </tr>
          </thead>
          <tbody data-testid="referrals-tbody">
            {rows.length === 0 && !loading && (
              <tr><td colSpan="6" className="p-6 text-center text-gray-500 italic" data-testid="referrals-empty">No referrals match the current filter.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-brand-border" data-testid={`admin-referral-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-semibold text-brand-navy text-xs">{r.homeowner_name || r.homeowner_email}</div>
                </td>
                <td className="px-3 py-2 hidden sm:table-cell text-xs">
                  {r.painter_name}
                  {r.painter_email && <div className="text-gray-500">{r.painter_email}</div>}
                </td>
                <td className="px-3 py-2 hidden md:table-cell text-xs capitalize">{r.method}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                    r.status === "activated" ? "bg-brand-success/15 text-brand-success" :
                    r.status === "declined" || r.status === "expired" ? "bg-gray-100 text-gray-700" :
                    "bg-brand-teal/10 text-brand-teal"
                  }`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 hidden lg:table-cell text-xs">
                  {r.discount_applied ? (
                    <span className="text-brand-success font-semibold">A${(r.discount_amount_aud || 0).toFixed(2)} · applied</span>
                  ) : (
                    <span className="text-gray-500">{Math.round(r.discount_percent || 20)}% pending</span>
                  )}
                </td>
                <td className="px-3 py-2 hidden md:table-cell text-xs">{new Date(r.created_at).toLocaleDateString("en-AU")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {report?.top_referrers?.length > 0 && (
        <div className="mt-6" data-testid="top-referrers">
          <h2 className="text-sm font-bold uppercase tracking-widest text-brand-teal mb-3">Top referring homeowners</h2>
          <ol className="space-y-1">
            {report.top_referrers.map((t, i) => (
              <li key={i} className="flex justify-between border-b border-brand-border py-1 text-sm">
                <span>{i + 1}. {t.homeowner}</span>
                <span className="text-xs text-gray-600">{t.activated}/{t.sent} activated</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-brand-border rounded-xl p-3">
      <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">{label}</div>
      <div className="text-2xl font-bold text-brand-navy mt-1">{value}</div>
    </div>
  );
}
